import { useState, useEffect } from 'react';
import map from 'lodash/map';
import find from 'lodash/find';
import head from 'lodash/head';
import { Row, Column, FormGroup, Label } from '@celltrak/ui-components';

import { useAction } from '../../../Common/hooks';
import { hideFlyout } from '../../../Dashboard/actions';
import { agenciesCreationSuccessAlert } from '../../constants';
import { fetchAgenciesList } from '../../actions';
import { createAgency } from '../../services';
import useAlert from '../../useAlert';

import fieldsDefinition from './fieldsDefinition';
import fieldsType from './fieldsType';
import validate from './validator';

const useAgenciesCreationForm = () => {
  const values = {};
  const { showAlert, hideAlert } = useAlert();
  const dispatchHideFlyout = useAction(hideFlyout);
  const dispatchFetchAgencies = useAction(fetchAgenciesList);
  const [isLoading, setIsLoading] = useState(false);
  const [fields, setFields] = useState([]);

  useEffect(() => {
    const resolveFieldDefinitions = async () => {
      const definitions = await fieldsDefinition();
      setFields(definitions);
    };

    resolveFieldDefinitions();
  }, []);

  const handleChange = id => value => {
    values[id] = value;
  };

  const handleCancel = () => {
    dispatchHideFlyout();
  };

  const handleCreate = async () => {
    setIsLoading(true);
    const message = find(validate(values));

    if (message) {
      showAlert({ message, color: 'error' });
      setIsLoading(false);

      return;
    }

    hideAlert();

    const response = await createAgency(values);

    if (response.errors?.length > 0) {
      const errorMessage = head(response.errors);
      showAlert({ color: 'error', icon: 'exclamation', message: errorMessage });
      setIsLoading(false);

      return;
    }

    setIsLoading(false);
    showAlert(agenciesCreationSuccessAlert);
    dispatchHideFlyout();
    dispatchFetchAgencies();
  };

  const createField = field => {
    const fieldProps = {
      id: `AgenciesCreationForm_${field.id}`,
      onInputChange: handleChange(field.id),
      size: 'sm',
      restrictions: field.restrictions,
    };

    return (
      <Column key={field.id} md="6">
        <FormGroup hint={field.hint}>
          <Label for={fieldProps.id} size={fieldProps.size} required>
            {field.label}
          </Label>
          {fieldsType(fieldProps)[field.type]}
        </FormGroup>
      </Column>
    );
  };

  const createFieldContainer = row => (
    <Row form key={`row_${row[0].id}`}>
      {map(row, createField)}
    </Row>
  );

  return {
    createFieldContainer,
    fields,
    handleCancel,
    handleCreate,
    isLoading,
  };
};

export default useAgenciesCreationForm;
